import React, { useEffect, useState } from 'react';

import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import { getLayout } from 'src/layouts/PanelLayout';
import { Col, Row } from 'reactstrap';
import { Card, CardTitle as CardTitleOrigin, CardBody } from 'src/components/common';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import authRequest from '../../../../lib/authRequest';
import { API_AFFILIATE_USER } from '../../../const/routes';
import { loadAffiliateBalance } from '../../../actions/app';
import Loader from '../../../components/common/loader';
import moment from 'moment';

const CardTitle = styled(CardTitleOrigin)`
  padding: 10px 10px 0px 10px;
`;

const BalanceCurrency = styled.span`
  font-size: 24px;
  color: #ffffff;
  font-weight: 300;
`;

const BalanceAmount = styled.span`
  font-size: 24px;
  color: #ffffff;
  font-weight: 300;
`;

const Attention = styled.p`
  background: #252736;
  font-size: 0.8em;
  padding: 10px;
`;

const CodeBlock = styled.pre`
  font-size: 0.8em;
  color: #ffffff;
  background: #252736;
  padding: 20px;
`;

const User = styled(Row)`
  background: #252736;
  padding: 20px;
  border-bottom: 1px;
`;

const NoData = styled.div`
  background: #252736;
  padding: 20px;
`;

const Affiliate = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [users, setUsers] = useState([]);

  const user = useSelector((state) => state.user);
  const token = useSelector((state) => state.user.token);
  const balances = useSelector((state) => state.app.affiliateBalances);

  useEffect(() => {
    if (!token) {
      return;
    }

    dispatch(
      loadAffiliateBalance(() => {
        setLoadingBalance(false);
      })
    );

    Promise.all([authRequest(API_AFFILIATE_USER, token)]).then((result) => {
      setUsers(result[0]);
      setLoadingUsers(false);
    });
  }, [token]);

  return (
    <div>
      <Head>
        <title>{t('pages.panel.affiliate.index.title')}</title>
      </Head>
      <Row>
        <Col lg={6}>
          <Card>
            <CardTitle>{t('pages.panel.affiliate.index.affiliateCard.title')}</CardTitle>
            <CardBody>
              <p>{t('pages.panel.affiliate.index.affiliateCard.line1')}</p>
              <CodeBlock>
                &lt;a href="{'https://trafficbooster.pro?ref=' + user.reference}"&gt;
                {t('pages.panel.affiliate.index.affiliateCard.hrefAnchor')}
                &lt;/a&gt;
              </CodeBlock>
              <p>{t('pages.panel.affiliate.index.affiliateCard.line2')}</p>

              <Attention>{t('pages.panel.affiliate.index.affiliateCard.attention')}</Attention>
            </CardBody>
          </Card>
        </Col>
        <Col lg={6}>
          <Card>
            <CardTitle>{t('pages.panel.affiliate.index.balanceCard.title')}</CardTitle>
            <CardBody>
              <Loader isLoading={loadingBalance}>
                {balances.length === 0 && <NoData>{t('pages.panel.affiliate.index.balanceCard.noData')}</NoData>}
                {balances.length > 0 &&
                  balances
                    .sort((a, b) => {
                      if (a.currency === 'USD' && b.currency !== 'USD') {
                        return -1;
                      }
                      return a.amount < b.amount;
                    })
                    .map((b) => (
                      <>
                        <Row>
                          <Col>
                            <BalanceCurrency>{b.currency}</BalanceCurrency>
                          </Col>
                          <Col>
                            <BalanceAmount>{b.amount.toFixed(2)}</BalanceAmount>
                          </Col>
                        </Row>
                      </>
                    ))}
              </Loader>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <CardTitle>{t('pages.panel.affiliate.index.userCard.title')}</CardTitle>
            <CardBody>
              <Loader isLoading={loadingUsers}>
                {users.length === 0 && <NoData>{t('pages.panel.affiliate.index.userCard.noData')}</NoData>}
                {users.length > 0 &&
                  users.map((u) => (
                    <>
                      <User>
                        <Col xs={3}>{moment(u.createdAt).format('HH:mm:ss DD-MM-YYYY')}</Col>
                        <Col xs={3}>{u.maskedEmail}</Col>
                        <Col xs={3}>{u.currency}</Col>
                        <Col xs={3}>{u.amount?.toFixed(2)}</Col>
                      </User>
                    </>
                  ))}
              </Loader>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export async function getStaticProps({ locale }) {
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
}

Affiliate.getLayout = getLayout;

export default Affiliate;
